import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, can, getUser, heldForApproval } from '../lib/api';
import { Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, useResource, useToast } from '../components/ui';
import { day, dayTime, number, retention } from '../lib/format';

/**
 * Retention due list.
 *
 * Every purpose, and every purpose at each collection point, whose retention
 * period ends within 30, 60 or 90 days for someone: how many people, when the
 * first and last periods end, who owns the decision (the grievance officer
 * named in the published notice), and the next step. On the end date each
 * consent expires on the ledger and the identifier is erased under the record
 * retention policy. Extending or erasing now is recorded with its reason.
 */

type Window = 30 | 60 | 90;

interface DueRow {
  kind: 'purpose' | 'collection_point';
  purposeId: string;
  purposeName: string;
  retentionDays: number | null;
  collectionPointId: string | null;
  collectionPointName: string | null;
  collectionPointChannel: string | null;
  people: number;
  earliest: string;
  latest: string;
  overdue: number;
  owner: { name: string; email: string } | null;
  /** Rule 8(2): erasure notices sent, and when erasure is permitted. */
  notice: { exempt: boolean; noticesSent: number; erasableNow: number; erasableFrom: string | null };
}

interface DueList { within: Window; asOf: string; rows: DueRow[] }

const EXTENSIONS: Array<{ days: number; label: string }> = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '180 days' },
  { days: 365, label: '1 year' },
  { days: 730, label: '2 years' },
  { days: 1095, label: '3 years' },
];

const CHANNEL_LABEL: Record<string, string> = {
  web: 'Web', app: 'App', api: 'API', offline: 'Offline', callcentre: 'Call centre', cookie: 'Cookie banner', form: 'Form',
};

function rowKey(r: DueRow): string {
  return r.kind === 'purpose' ? r.purposeId : `${r.collectionPointId}/${r.purposeId}`;
}

export default function RetentionDue() {
  const user = getUser();
  const canAct = can(user, 'purposes:edit');
  const [within, setWithin] = useState<Window>(30);
  const [reload, setReload] = useState(0);
  const [acting, setActing] = useState<{ row: DueRow; action: 'extend' | 'erase' } | null>(null);
  const [notifying, setNotifying] = useState<DueRow | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const data = useResource<DueList>(() => api.get(`/v1/retention/due?within=${within}`), [within, reload]);
  const rows = data.data?.rows ?? [];
  const purposeRows = rows.filter((r) => r.kind === 'purpose');
  const people = purposeRows.reduce((n, r) => n + r.people, 0);

  return (
    <>
      <PageHead
        title="Retention due"
        description="Purposes and collection points whose retention period ends in the window. Each person is told at least 48 hours before erasure (Rule 8(2)); on the end date, once that wait is over, the consent expires and the identifier is erased under the record retention policy."
      >
        {canAct && <button onClick={() => setSettingsOpen(true)}>Erasure notice settings</button>}
      </PageHead>
      <ErrorNote error={data.error} />

      <div className="tabs">
        {([30, 60, 90] as Window[]).map((w) => (
          <button key={w} className={within === w ? 'active' : ''} onClick={() => setWithin(w)}>
            Within {w} days
            {within === w && data.data ? <span className="tab-count">{purposeRows.length}</span> : null}
          </button>
        ))}
        <span className="small muted" style={{ marginLeft: 'auto', alignSelf: 'center' }}>
          {data.data ? `${number(people)} ${people === 1 ? 'person' : 'people'} across ${purposeRows.length} ${purposeRows.length === 1 ? 'purpose' : 'purposes'}` : ''}
        </span>
      </div>

      <Card>
        {data.loading && !data.data && <Loading what="Loading the due list" />}
        {data.data && rows.length === 0 && <Empty>No retention period ends within {within} days.</Empty>}
        {rows.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Purpose or collection point</th>
                  <th className="num">People</th>
                  <th>Period ends</th>
                  <th>Notice sent</th>
                  <th>Erasable from</th>
                  <th>Owner</th>
                  <th>Next step</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={rowKey(r)}>
                    <td>
                      {r.kind === 'purpose' ? (
                        <>
                          <strong>{r.purposeName}</strong>
                          <div className="small muted">Purpose · kept for {retention(r.retentionDays)}</div>
                        </>
                      ) : (
                        <>
                          <strong>{r.collectionPointName}</strong>
                          <div className="small muted">
                            {CHANNEL_LABEL[r.collectionPointChannel ?? ''] ?? r.collectionPointChannel} collection point · {r.purposeName}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="num">{number(r.people)}</td>
                    <td className="small">
                      {r.earliest === r.latest ? day(r.earliest) : `${day(r.earliest)} to ${day(r.latest)}`}
                      {r.overdue > 0 && <> <Pill tone="bad">{r.overdue} past the date</Pill></>}
                    </td>
                    <td className="small">
                      {r.notice.exempt
                        ? <span className="muted">Exempt</span>
                        : `${number(r.notice.noticesSent)} of ${number(r.people)}`}
                    </td>
                    <td className="small">
                      {r.notice.exempt
                        ? 'Now'
                        : r.notice.erasableNow === r.people
                          ? 'Now'
                          : r.notice.erasableFrom
                            ? dayTime(r.notice.erasableFrom)
                            : <span className="muted">After the notice</span>}
                    </td>
                    <td className="small">
                      {r.owner ? (
                        <>
                          {r.owner.name || r.owner.email}
                          {r.owner.name && <div className="muted">{r.owner.email}</div>}
                        </>
                      ) : (
                        <span className="muted">No grievance officer on the published notice</span>
                      )}
                    </td>
                    <td>
                      {canAct ? (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {!r.notice.exempt && r.notice.noticesSent < r.people && (
                            <button className="small primary" onClick={() => setNotifying(r)}>Send notices</button>
                          )}
                          <button className="small" onClick={() => setActing({ row: r, action: 'extend' })}>Extend</button>
                          <button className="small danger" onClick={() => setActing({ row: r, action: 'erase' })}>Erase</button>
                        </div>
                      ) : (
                        <span className="small muted">Expires on the date</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="small muted" style={{ marginTop: 12 }}>
        Reminders go to the owner 30 days and 7 days before the first period ends. Erasure notices go to each person by email or SMS ahead of the end date, through the organisation's channels. The record retention policy is under <Link to="/settings/security">Security</Link>.
      </div>

      {notifying && (
        <NotifyModal
          row={notifying}
          within={within}
          onClose={() => setNotifying(null)}
          onDone={() => { setNotifying(null); setReload((n) => n + 1); }}
        />
      )}
      {settingsOpen && (
        <NoticeSettingsModal onClose={() => setSettingsOpen(false)} onDone={() => { setSettingsOpen(false); setReload((n) => n + 1); }} />
      )}

      {acting && (
        <ActionModal
          row={acting.row}
          action={acting.action}
          within={within}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); setReload((n) => n + 1); }}
        />
      )}
    </>
  );
}

function ActionModal({ row, action, within, onClose, onDone }: {
  row: DueRow; action: 'extend' | 'erase'; within: Window; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [days, setDays] = useState(365);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const scope = row.kind === 'purpose'
    ? `${row.purposeName}, every collection point`
    : `${row.purposeName} at ${row.collectionPointName}`;
  const ok = reason.trim().length >= 5;

  async function submit() {
    setBusy(true);
    try {
      const body = { reason: reason.trim(), within, collectionPointId: row.collectionPointId };
      if (action === 'extend') {
        const r = await api.post<{ extended: number; earliest: string | null }>(`/v1/retention/due/${row.purposeId}/extend`, { ...body, days });
        toast(`${number(r.extended)} ${r.extended === 1 ? 'consent' : 'consents'} extended${r.earliest ? `; first period now ends ${day(r.earliest)}` : ''}.`, 'ok');
      } else {
        const r = await api.post<{ expired: number; erased: number; held?: number }>(`/v1/retention/due/${row.purposeId}/erase`, body);
        if (heldForApproval(r)) toast('Sent for approval. A second person must approve the erasure on the Approvals page.', 'ok');
        else toast(`${number(r.expired)} ${r.expired === 1 ? 'consent' : 'consents'} expired on the ledger; ${number(r.erased)} ${r.erased === 1 ? 'identifier' : 'identifiers'} erased under the record policy${r.held ? `; ${number(r.held)} held until 48 hours after their notice` : ''}.`, 'ok');
      }
      onDone();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={action === 'extend' ? 'Extend the retention period' : 'Erase now'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className={action === 'erase' ? 'danger' : 'primary'} onClick={submit} disabled={busy || !ok}>
            {busy ? 'Working' : action === 'extend' ? 'Extend' : 'Erase'}
          </button>
        </>
      }
    >
      <dl className="kv">
        <dt>Scope</dt><dd>{scope}</dd>
        <dt>People</dt><dd>{number(row.people)}, period ending {row.earliest === row.latest ? day(row.earliest) : `${day(row.earliest)} to ${day(row.latest)}`}</dd>
        <dt>Owner</dt><dd>{row.owner ? `${row.owner.name || row.owner.email}` : 'Not named on the notice'}</dd>
      </dl>
      {action === 'extend' ? (
        <Field label="Extend by" help="Added to each consent's current end date. The consent record on the ledger is unchanged; the extension and its reason go to the audit log.">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {EXTENSIONS.map((x) => <option key={x.days} value={x.days}>{x.label}</option>)}
          </select>
        </Field>
      ) : (
        <div className="note warn" style={{ marginBottom: 10 }}>
          Each consent in scope expires on the ledger today with your name and reason, processors are told as for any expiry, and the record retention policy runs. This cannot be undone.
        </div>
      )}
      <Field label="Reason" help="Recorded in the audit log. At least five characters.">
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

/** Sends the Rule 8(2) erasure notices for one row now; erasure is permitted 48 hours later. */
function NotifyModal({ row, within, onClose, onDone }: { row: DueRow; within: Window; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const scope = row.kind === 'purpose' ? `${row.purposeName}, every collection point` : `${row.purposeName} at ${row.collectionPointName}`;
  const left = row.people - row.notice.noticesSent;

  async function send() {
    setBusy(true);
    try {
      const r = await api.post<{ sent: number; noContact: number; failed: number; erasableFrom: string | null }>(
        `/v1/retention/due/${row.purposeId}/notify`, { within, collectionPointId: row.collectionPointId },
      );
      const parts = [`${number(r.sent)} sent`];
      if (r.noContact) parts.push(`${number(r.noContact)} with no contact held`);
      if (r.failed) parts.push(`${number(r.failed)} failed, retried by the retention job`);
      toast(`Erasure notices: ${parts.join(', ')}.${r.erasableFrom ? ` Erasable from ${dayTime(r.erasableFrom)}.` : ''}`, r.failed ? 'bad' : 'ok');
      onDone();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Send erasure notices"
      onClose={onClose}
      footer={<><button onClick={onClose} disabled={busy}>Cancel</button><button className="primary" onClick={send} disabled={busy}>{busy ? 'Sending' : 'Send notices'}</button></>}
    >
      <dl className="kv">
        <dt>Scope</dt><dd>{scope}</dd>
        <dt>People not yet told</dt><dd>{number(left)}</dd>
        <dt>Period ends</dt><dd>{row.earliest === row.latest ? day(row.earliest) : `${day(row.earliest)} to ${day(row.latest)}`}</dd>
      </dl>
      <div className="note" style={{ marginBottom: 6 }}>
        Each person is told by email or SMS that their data for this purpose will be erased, and that signing in or contacting the organisation keeps it. Erasure is permitted 48 hours after each notice (Rule 8(2)). People with no email address or mobile number held are recorded as not reachable.
      </div>
    </Modal>
  );
}

interface PurposeOption { id: string; status: string; current: { name: string } }

/** How far ahead the notice goes, and which purposes are exempt from it. */
function NoticeSettingsModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const settings = useResource<{ leadHours: number; exemptPurposeIds: string[] }>(() => api.get('/v1/retention/erasure-notices/settings'));
  const purposes = useResource<{ purposes: PurposeOption[] }>(() => api.get('/v1/purposes'));
  const [lead, setLead] = useState<number | null>(null);
  const [exempt, setExempt] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const leadValue = lead ?? settings.data?.leadHours ?? 72;
  const exemptValue = exempt ?? settings.data?.exemptPurposeIds ?? [];

  async function save() {
    setBusy(true);
    try {
      await api.put('/v1/retention/erasure-notices/settings', { leadHours: leadValue, exemptPurposeIds: exemptValue });
      toast('Erasure notice settings saved.', 'ok');
      onDone();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Erasure notice settings"
      onClose={onClose}
      footer={<><button onClick={onClose} disabled={busy}>Cancel</button><button className="primary" onClick={save} disabled={busy || !settings.data}>{busy ? 'Saving' : 'Save'}</button></>}
    >
      <ErrorNote error={settings.error ?? purposes.error} />
      <Field label="Send the notice (hours before the period ends)" help="At least 48. The retention job sends notices for periods ending within this many hours.">
        <input type="number" min={48} max={720} value={leadValue} onChange={(e) => setLead(Number(e.target.value))} />
      </Field>
      <Field label="Purposes exempt from the notice" help="For an organisation outside the Rule 8 classes, or data kept under another law. Exempt purposes are erased on the date with no notice.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(purposes.data?.purposes ?? []).filter((p) => p.status !== 'retired').map((p) => (
            <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 400 }}>
              <input
                type="checkbox" style={{ width: 'auto' }}
                checked={exemptValue.includes(p.id)}
                onChange={(e) => setExempt(e.target.checked ? [...exemptValue, p.id] : exemptValue.filter((x) => x !== p.id))}
              />
              {p.current.name}
            </label>
          ))}
        </div>
      </Field>
    </Modal>
  );
}
